/**
 * HockeyGallery — reusable gallery for a named collection.
 * Props:
 *   collection   string  e.g. "wharf" | "camels"
 *   title        string  page heading
 *   subtitle     string  subheading
 *   accentColor  string  hex color
 *   darkBg       string  page background hex
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const LOW_RES_MAX = 800;

// Mobile detection — iOS + Android both support Web Share API for saving to Photos/Gallery
const isIOS    = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isMobile = isIOS || (typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent));

function getToken() {
  return localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken') || '';
}
function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}
function triggerDownload(url) {
  // Direct link approach — works on iOS Safari (Content-Disposition: attachment
  // triggers the native save/share sheet) and all desktop browsers.
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';   // opens in new tab on iOS, triggers save sheet
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// Save a single photo to iPhone Photos app via Web Share API.
// Falls back to triggerDownload if Web Share isn't available.
async function saveToPhotos(downloadUrl, filename = 'photo.jpg') {
  try {
    const resp = await fetch(downloadUrl, { headers: authHeaders() });
    const blob = await resp.blob();
    const ext  = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
    const name = filename.includes('.') ? filename : `${filename}.${ext}`;
    const file = new File([blob], name, { type: blob.type });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title: 'Save Photo' });
    } else {
      triggerDownload(downloadUrl);
    }
  } catch (err) {
    // User cancelled share sheet — not an error worth logging loudly
    if (err.name !== 'AbortError') console.error('saveToPhotos:', err);
  }
}

// ── Lightbox ─────────────────────────────────────────────────────────────────
function Lightbox({ img, collection, accent, onClose, onPrev, onNext, hasPrev, hasNext, onSaveToPhotos }) {
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft'  && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  const base = `/api/core/gallery/${collection}`;
  return (
    <div style={ls.overlay} onClick={onClose}>
      <div style={ls.box} onClick={e => e.stopPropagation()}>
        <button style={ls.close} onClick={onClose}>✕</button>
        {hasPrev && <button style={{ ...ls.nav, left: 12 }} onClick={onPrev}>‹</button>}
        {hasNext && <button style={{ ...ls.nav, right: 12 }} onClick={onNext}>›</button>}
        <img src={img.url} alt={img.title} style={ls.img} />
        {img.title && <p style={ls.caption}>{img.title}</p>}
        <div style={ls.actions}>
          {isMobile ? (
            <button style={{ ...ls.dlBtn, background: accent, border: 'none' }} onClick={() =>
              onSaveToPhotos(`${base}/${img.id}/download/?quality=full`, `photo-${img.id}.jpg`)
}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 5, verticalAlign: 'middle' }}>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Save to Gallery
            </button>
          ) : (
            <>
              <button style={{ ...ls.dlBtn, background: accent, border: 'none' }} onClick={() =>
                triggerDownload(`${base}/${img.id}/download/?quality=full`)
              }>⬇ Full Res</button>
              <button style={{ ...ls.dlBtn, background: 'transparent', border: `1px solid ${accent}`, color: accent }} onClick={() =>
                triggerDownload(`${base}/${img.id}/download/?quality=low`)
              }>⬇ Low Res</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
function useGridCols() {
  const getCol = () => {
    const w = window.innerWidth;
    if (w < 480)  return 2;
    if (w < 768)  return 2;
    if (w < 1024) return 3;
    return 4;
  };
  const [cols, setCols] = useState(getCol);
  useEffect(() => {
    const handler = () => setCols(getCol());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return cols;
}

export default function HockeyGallery({ collection, title, subtitle, accentColor = '#E87722', darkBg = '#1a1a1a' }) {
  const { user }   = useAuth();
  const isAdmin    = user?.is_staff || user?.is_superuser;
  const API_BASE   = `/api/core/gallery/${collection}`;
  const gridCols   = useGridCols();

  const [images,      setImages]      = useState([]);
  const [page,        setPage]        = useState(1);
  const [numPages,    setNumPages]    = useState(1);
  const [count,       setCount]       = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [lbIdx,       setLbIdx]       = useState(null);

  const [uploadFiles,    setUploadFiles]    = useState([]);
  const [uploadTitle,    setUploadTitle]    = useState('');
  const [uploadBusy,     setUploadBusy]     = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);   // 0-100
  const [uploadStatus,   setUploadStatus]   = useState('');  // e.g. "Batch 2 of 19"
  const [uploadMsg,      setUploadMsg]      = useState(null);
  const [dlAllBusy,      setDlAllBusy]      = useState(null); // 'full' | 'low' | null
  const [statusMsg,      setStatusMsg]      = useState(null);
  const [dragOver,       setDragOver]       = useState(false);
  const [shareCopied,    setShareCopied]    = useState(false);

  const fetchImages = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/?page=${p}`);
      const data = await res.json();
      setImages(data.results || []);
      setNumPages(data.num_pages || 1);
      setCount(data.count || 0);
      setPage(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { fetchImages(1); }, [fetchImages]);

  const BATCH_SIZE = 10;

  const handleUpload = async () => {
    if (!uploadFiles.length) { setUploadMsg({ ok: false, text: 'Select at least one image first.' }); return; }

    const files = Array.from(uploadFiles);
    const batches = [];
    for (let i = 0; i < files.length; i += BATCH_SIZE) batches.push(files.slice(i, i + BATCH_SIZE));

    setUploadBusy(true); setUploadMsg(null); setUploadProgress(0); setUploadStatus('');

    let totalUploaded = 0;
    const errors = [];

    for (let b = 0; b < batches.length; b++) {
      const batch = batches[b];
      setUploadStatus(`Batch ${b + 1} of ${batches.length} (${totalUploaded + batch.length} / ${files.length} files)`);

      const form = new FormData();
      batch.forEach(f => form.append('images', f));
      if (uploadTitle) form.append('title', uploadTitle);

      // Base progress for completed batches
      const baseProgress = Math.round((totalUploaded / files.length) * 100);

      try {
        const { data } = await api.post(`/core/gallery/${collection}/upload/`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000, // 2 min per batch
          onUploadProgress: (e) => {
            if (e.total) {
              const batchProgress = Math.round((e.loaded / e.total) * (batch.length / files.length) * 100);
              setUploadProgress(Math.min(99, baseProgress + batchProgress));
            }
          },
        });
        totalUploaded += data.uploaded.length;
      } catch (err) {
        const msg = err.response?.data?.error || err.response?.data?.detail || `Batch ${b + 1} failed`;
        errors.push(msg);
      }
    }

    setUploadProgress(100);
    setUploadStatus('');

    if (errors.length === 0) {
      setUploadMsg({ ok: true, text: `✓ Uploaded ${totalUploaded} image${totalUploaded !== 1 ? 's' : ''} successfully` });
    } else if (totalUploaded > 0) {
      setUploadMsg({ ok: false, text: `Uploaded ${totalUploaded} of ${files.length} — ${errors.length} batch(es) failed` });
    } else {
      setUploadMsg({ ok: false, text: errors[0] || 'Upload failed' });
    }

    setUploadFiles([]); setUploadTitle('');
    setTimeout(() => setUploadProgress(0), 1500);
    fetchImages(1);
    setUploadBusy(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      const res = await fetch(`${API_BASE}/${id}/delete/`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) { setStatusMsg('Deleted.'); fetchImages(page); }
      else        { setStatusMsg('Delete failed.'); }
    } catch { setStatusMsg('Network error.'); }
    setTimeout(() => setStatusMsg(null), 3000);
  };

  const handleDownloadAll = (quality) => {
    if (quality === 'full' && count > 50) {
      if (!window.confirm(`Download all ${count} full-resolution photos as a ZIP? This may be a large file.`)) return;
    }
    setDlAllBusy(quality);
    triggerDownload(`${API_BASE}/download-all/?quality=${quality}`);
    // Clear busy state after a short delay — we can't detect when browser finishes
    setTimeout(() => setDlAllBusy(null), 3000);
  };

  const handleShare = async () => {
    const galleryUrl = `${window.location.origin}/${collection}`;
    if (navigator.share) {
      try {
        await navigator.share({ title, url: galleryUrl });
      } catch (e) {
        if (e.name !== 'AbortError') navigator.clipboard?.writeText(galleryUrl);
      }
    } else {
      await navigator.clipboard.writeText(galleryUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    }
  };

  const s = makeStyles(accentColor, darkBg);
  // Responsive grid: 2 cols on mobile, 3 on tablet, 4 on desktop
  const colGap = gridCols <= 2 ? 10 : 18;
  s.grid = { ...s.grid, columns: gridCols, columnGap: colGap };
  s.card = { ...s.card, marginBottom: colGap };

  return (
    <div style={s.page}>
      {/* HEADER */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>{title}</h1>
          <p style={s.subtitle}>{subtitle}{count > 0 && ` · ${count} image${count !== 1 ? 's' : ''}`}</p>
        </div>
        {count > 0 && (
          <>
            <div style={s.dlRow}>
              <span style={s.dlLabel}>Download All:</span>
              <button style={{ ...s.btnPrimary, opacity: dlAllBusy === 'full' ? 0.6 : 1 }} disabled={dlAllBusy === 'full'} onClick={() => handleDownloadAll('full')}>
                {dlAllBusy === 'full' ? '⏳ Preparing…' : '⬇ Full Res'}
              </button>
              <button style={{ ...s.btnOutline, opacity: dlAllBusy === 'low' ? 0.6 : 1 }} disabled={dlAllBusy === 'low'} onClick={() => handleDownloadAll('low')}>
                {dlAllBusy === 'low' ? '⏳ Preparing…' : '⬇ Low Res'}
              </button>
              <button style={{ ...s.btnOutline, minWidth: 90 }} onClick={handleShare}>
                {shareCopied ? '✅ Copied!' : '🔗 Share'}
              </button>
            </div>
            {isMobile && (
              <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 6 }}>
                💡 Use the Save button on each photo to save directly to your Photos app
              </p>
            )}
          </>
        )}
      </div>

      {/* ADMIN UPLOAD */}
      {isAdmin && (
        <div style={s.uploadBox}>
          <h3 style={s.uploadTitle}>Upload Images</h3>
          {/* Drop zone */}
          <label
            style={{
              ...s.dropZone,
              borderColor: dragOver ? accentColor : `${accentColor}55`,
              background: dragOver ? `${accentColor}18` : 'rgba(255,255,255,0.03)',
            }}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => {
              e.preventDefault();
              setDragOver(false);
              const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
              if (files.length) setUploadFiles(files);
            }}
          >
            <input type="file" accept="image/*" multiple style={{ display: 'none' }}
              onChange={e => setUploadFiles(Array.from(e.target.files))} />
            <span style={{ fontSize: 32 }}>📸</span>
            <span style={{ color: '#9ca3af', fontSize: 14 }}>
              {uploadFiles.length
                ? `${uploadFiles.length} file${uploadFiles.length !== 1 ? 's' : ''} selected`
                : <>Drag & drop images here, or <span style={{ color: accentColor, fontWeight: 600 }}>click to browse</span></>
              }
            </span>
            {uploadFiles.length > 0 && (
              <span style={{ color: '#6b7280', fontSize: 12 }}>Click to change selection</span>
            )}
          </label>
          {/* Caption + Upload row */}
          <div style={s.uploadRow}>
            <input
              type="text"
              placeholder="Caption (optional)"
              value={uploadTitle}
              onChange={e => setUploadTitle(e.target.value)}
              style={{ ...s.input, flex: 1 }}
            />
            <button
              style={{ ...s.btnPrimary, opacity: uploadBusy ? 0.6 : 1, cursor: uploadBusy ? 'not-allowed' : 'pointer' }}
              disabled={uploadBusy}
              onClick={handleUpload}
            >
              {uploadBusy ? 'Uploading…' : `Upload${uploadFiles.length ? ` (${uploadFiles.length})` : ''}`}
            </button>
          </div>
          {/* Progress bar */}
          {uploadBusy && (
            <div style={{ marginTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: '#9ca3af', fontSize: 12 }}>{uploadStatus}</span>
                <span style={{ color: '#9ca3af', fontSize: 12 }}>{uploadProgress}%</span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 6,
                  background: accentColor,
                  width: `${uploadProgress}%`,
                  transition: 'width 0.3s ease',
                }} />
              </div>
            </div>
          )}
          {uploadMsg && <p style={{ color: uploadMsg.ok ? '#4ade80' : '#f87171', fontSize: 13, marginTop: 8 }}>{uploadMsg.text}</p>}
          {statusMsg  && <p style={{ color: '#f87171', fontSize: 13, marginTop: 4 }}>{statusMsg}</p>}
        </div>
      )}

      {/* GRID */}
      {loading ? (
        <p style={s.empty}>Loading…</p>
      ) : images.length === 0 ? (
        <p style={s.empty}>No images yet{isAdmin ? ' — upload some above!' : '.'}</p>
      ) : (
        <div style={s.grid}>
          {images.map((img, idx) => (
            <div key={img.id} style={s.card}>
              <div
                style={s.imgWrap}
                onClick={() => setLbIdx(idx)}
                onMouseEnter={e => e.currentTarget.querySelector('img').style.transform = 'scale(1.06)'}
                onMouseLeave={e => e.currentTarget.querySelector('img').style.transform = 'scale(1)'}
              >
                <img src={img.thumbnail_url || img.url} alt={img.title} style={s.thumb} loading="lazy" />
              </div>
              {img.title && <p style={s.cardTitle}>{img.title}</p>}
              <div style={s.cardActions}>
                {isMobile ? (
                  <button style={s.btnSmPrimary} onClick={() =>
                    saveToPhotos(`${API_BASE}/${img.id}/download/?quality=full`, `photo-${img.id}.jpg`)
}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, verticalAlign: 'middle' }}>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Save
                  </button>
                ) : (
                  <>
                    <button style={s.btnSmPrimary} onClick={() =>
                      triggerDownload(`${API_BASE}/${img.id}/download/?quality=full`)
                    }>⬇ Full</button>
                    <button style={s.btnSmOutline} onClick={() =>
                      triggerDownload(`${API_BASE}/${img.id}/download/?quality=low`)
                    }>⬇ Low</button>
                  </>
                )}
                {isAdmin && (
                  <button style={s.btnDelete} onClick={() => handleDelete(img.id)}>🗑</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PAGINATION */}
      {numPages > 1 && (
        <div style={s.pagination}>
          <button style={s.btnOutline} disabled={page <= 1}        onClick={() => fetchImages(page - 1)}>← Prev</button>
          <span style={s.pageInfo}>Page {page} of {numPages}</span>
          <button style={s.btnOutline} disabled={page >= numPages} onClick={() => fetchImages(page + 1)}>Next →</button>
        </div>
      )}

      {/* LIGHTBOX */}
      {lbIdx !== null && images[lbIdx] && (
        <Lightbox
          img={images[lbIdx]}
          collection={collection}
          accent={accentColor}
          onClose={() => setLbIdx(null)}
          onPrev={() => setLbIdx(i => Math.max(0, i - 1))}
          onNext={() => setLbIdx(i => Math.min(images.length - 1, i + 1))}
          hasPrev={lbIdx > 0}
          hasNext={lbIdx < images.length - 1}
          onSaveToPhotos={saveToPhotos}
        />
      )}
    </div>
  );
}

// ── Styles factory ────────────────────────────────────────────────────────────
function makeStyles(accent, bg) {
  const cardBg = bg === '#1a1a1a' ? '#2d2d2d' : '#1e2a3a';
  return {
    page:        { minHeight: '100vh', background: bg, color: '#f0ece4', padding: '100px 24px 60px', fontFamily: "'Inter','Segoe UI',sans-serif" },
    header:      { maxWidth: 1200, margin: '0 auto 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, borderBottom: `2px solid ${accent}`, paddingBottom: 20 },
    title:       { fontSize: 34, fontWeight: 800, color: accent, margin: 0, letterSpacing: '-0.02em' },
    subtitle:    { color: '#9ca3af', margin: '4px 0 0', fontSize: 14 },
    dlRow:       { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
    dlLabel:     { color: '#9ca3af', fontSize: 13 },
    uploadBox:   { maxWidth: 1200, margin: '0 auto 28px', background: cardBg, border: `1px solid ${accent}44`, borderRadius: 12, padding: '18px 22px' },
    uploadTitle: { margin: '0 0 12px', color: accent, fontSize: 14, fontWeight: 700 },
    dropZone:    { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', minHeight: 110, border: '2px dashed', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s ease', marginBottom: 12, boxSizing: 'border-box', padding: '20px 16px' },
    uploadRow:   { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
    input:       { flex: 1, minWidth: 160, padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: '#f0ece4', fontSize: 13, outline: 'none' },
    fileLabel:   { padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: '#d1d5db', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' },
    empty:       { textAlign: 'center', color: '#9ca3af', marginTop: 60, fontSize: 16 },
    grid:        { maxWidth: 1200, margin: '0 auto', columns: 4, columnGap: 18 },
    card:        { display: 'inline-block', width: '100%', marginBottom: 18, background: cardBg, borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.07)', breakInside: 'avoid' },
    imgWrap:     { position: 'relative', cursor: 'pointer', overflow: 'hidden', background: '#111' },
    thumb:       { width: '100%', height: 'auto', display: 'block', transition: 'transform 0.25s ease' },
    cardTitle:   { padding: '10px 12px 4px', fontSize: 12, color: '#d1d5db', margin: 0, lineHeight: 1.4 },
    cardActions: { display: 'flex', gap: 8, padding: '8px 12px 12px' },
    pagination:  { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 40, maxWidth: 1200, margin: '40px auto 0' },
    pageInfo:    { color: '#9ca3af', fontSize: 13 },
    btnPrimary:  { background: accent, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
    btnOutline:  { background: 'transparent', color: accent, border: `1px solid ${accent}`, borderRadius: 8, padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
    btnSmPrimary:{ flex: 1, background: accent, color: '#fff', border: 'none', borderRadius: 6, padding: '6px 0', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    btnSmOutline:{ flex: 1, background: 'transparent', color: accent, border: `1px solid ${accent}`, borderRadius: 6, padding: '6px 0', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
    btnDelete:   { background: 'rgba(239,68,68,0.15)', color: '#f87171', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '6px 10px', fontSize: 12, cursor: 'pointer' },
  };
}

// ── Lightbox styles ───────────────────────────────────────────────────────────
const ls = {
  overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.93)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  box:      { position: 'relative', maxWidth: '90vw', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  img:      { maxWidth: '85vw', maxHeight: '75vh', objectFit: 'contain', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.8)' },
  caption:  { color: '#d1d5db', fontSize: 13, marginTop: 12, textAlign: 'center' },
  actions:  { display: 'flex', gap: 10, marginTop: 16 },
  dlBtn:    { color: '#fff', borderRadius: 8, padding: '8px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  close:    { position: 'absolute', top: -40, right: 0, background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', padding: 4 },
  nav:      { position: 'absolute', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 32, borderRadius: 6, padding: '8px 14px', cursor: 'pointer', lineHeight: 1 },
};
